import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, SupportTicket } from '@nexus/shared/types';
import { toast } from './toastStore';

interface SupportState {
  tickets: SupportTicket[];
  selected: SupportTicket | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  fetch: (params?: Record<string, string | number | undefined>) => Promise<void>;
  fetchOne: (id: string) => Promise<void>;
  create: (data: Record<string, unknown>) => Promise<SupportTicket>;
  update: (id: string, data: Record<string, unknown>) => Promise<void>;
  resolve: (id: string) => Promise<void>;
  bulkUpdate: (ids: string[], updates: Record<string, unknown>) => Promise<void>;
  clearError: () => void;
}

function convert(t: Record<string, unknown>): SupportTicket {
  return {
    ...t,
    createdAt: fromUnix(t.createdAt as number),
    updatedAt: fromUnix(t.updatedAt as number),
    slaDeadline: t.slaDeadline ? fromUnix(t.slaDeadline as number) : undefined,
    resolvedAt: t.resolvedAt ? fromUnix(t.resolvedAt as number) : undefined,
  } as SupportTicket;
}

export const useSupportStore = create<SupportState>((set, get) => ({
  tickets: [],
  selected: null,
  total: 0,
  isLoading: false,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<SupportTicket[]>>(`/api/support/tickets${toQueryString(params)}`);
      set({
        tickets: (res.data ?? []).map(t => convert(t as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchOne: async (id) => {
    try {
      const res = await api.get<ApiResponse<SupportTicket>>(`/api/support/tickets/${id}`);
      if (res.data) set({ selected: convert(res.data as unknown as Record<string, unknown>) });
    } catch (err: unknown) {
      set({ error: (err as Error).message });
    }
  },

  create: async (data) => {
    try {
      const res = await api.post<ApiResponse<SupportTicket>>('/api/support/tickets', data);
      const ticket = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ tickets: [ticket, ...s.tickets], total: s.total + 1 }));
      toast.success('Ticket created', (data as Record<string, string>).subject);
      return ticket;
    } catch (err) {
      toast.error('Failed to create ticket', (err as Error).message);
      throw err;
    }
  },

  update: async (id, data) => {
    try {
      const res = await api.patch<ApiResponse<SupportTicket>>(`/api/support/tickets/${id}`, data);
      const ticket = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({
        tickets: s.tickets.map((t) => (t.id === id ? ticket : t)),
        selected: s.selected?.id === id ? ticket : s.selected,
      }));
      toast.success('Ticket updated');
    } catch (err) {
      toast.error('Failed to update ticket', (err as Error).message);
      throw err;
    }
  },

  resolve: async (id) => {
    try {
      const res = await api.post<ApiResponse<SupportTicket>>(`/api/support/tickets/${id}/resolve`);
      const ticket = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({
        tickets: s.tickets.map((t) => (t.id === id ? ticket : t)),
        selected: s.selected?.id === id ? ticket : s.selected,
      }));
      toast.success('Ticket resolved');
    } catch (err) {
      toast.error('Failed to resolve ticket', (err as Error).message);
      throw err;
    }
  },

  bulkUpdate: async (ids, updates) => {
    try {
      await api.patch('/api/support/tickets/bulk', { ids, updates });
      toast.success(`Updated ${ids.length} ticket${ids.length !== 1 ? 's' : ''}`);
      get().fetch();
    } catch (err) {
      toast.error('Bulk update failed', (err as Error).message);
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
